import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'

import { IMappedGeoMappingItem } from 'partner360/components/filters'
import { hasFlag } from 'partner360/features/topPartnersManagement/helpers/hasFlag'
import type { IP360PartnerFlags } from 'partner360/models/p360PartnerFlags/IP360PartnerFlags'

import { IFinancialCockpitUserWithScope } from '../api/types'
import {
  ICockpitCompletenessData,
  ICockpitFilterState,
  ICockpitPartnerData,
  NOT_PART_OF_GLOBAL_PARTNER,
  TCockpitMandatoryStatus,
  TCockpitPlannedStatus,
  TCockpitRtm,
  TMdfStatus,
} from '../model/types'

import {
  isKnownCockpitAmplifyMembership,
  OTHER_COCKPIT_AMPLIFY_MEMBERSHIP,
  parseCockpitAmplifyMemberships,
} from './amplifyMemberships'

interface IParams {
  partnerData: ICockpitPartnerData[]
  selectedRtm: TCockpitRtm
  selectedOffering: ProgramTypes
  completenessData: ICockpitCompletenessData[]
  filter: ICockpitFilterState
  pbmUsers: IFinancialCockpitUserWithScope[]
  managerUsers: IFinancialCockpitUserWithScope[]
  geoMappingItems: IMappedGeoMappingItem[]
  partnerClassFlags: IP360PartnerFlags[]
}

const matchesRtm = (partner: ICockpitPartnerData, selectedRtm: TCockpitRtm) =>
  selectedRtm === 'resellers' ? partner.IsCommercial === 1 : partner.IsDistributor === 1

const matchesGeoMappings = (
  partner: ICockpitPartnerData,
  geoMappings: string[],
  geoMappingItems: IMappedGeoMappingItem[]
) => {
  if (geoMappings.length === 0) {
    return true
  }

  const countryCode = partner.CountryCode.trim().toLowerCase()

  return geoMappings.some((id) =>
    geoMappingItems
      .find((item) => item.itemId === id)
      ?.countries.some((country) => country.trim().toLowerCase() === countryCode)
  )
}

const matchesAmplifyMemberships = (partner: ICockpitPartnerData, amplifyMemberships: string[]) => {
  if (amplifyMemberships.length === 0) {
    return true
  }

  const partnerMemberships = parseCockpitAmplifyMemberships(partner.AmplifyTrackWithMembership)

  return amplifyMemberships.some((selectedMembership) =>
    selectedMembership === OTHER_COCKPIT_AMPLIFY_MEMBERSHIP
      ? partnerMemberships.some((membership) => !isKnownCockpitAmplifyMembership(membership))
      : partnerMemberships.includes(selectedMembership)
  )
}

const matchesPartnerClasses = (
  partner: ICockpitPartnerData,
  partnerClasses: string[],
  partnerClassFlags: IP360PartnerFlags[]
) =>
  partnerClasses.length === 0 ||
  partnerClasses.some((partnerFlag) => {
    const flag = partnerClassFlags.find((f) => f.PartnerFlag === partnerFlag)
    return flag !== undefined && hasFlag(partner.BitClass, flag.BitValue)
  })

const matchesUserEmail = (
  partner: ICockpitPartnerData,
  emails: string[],
  users: IFinancialCockpitUserWithScope[]
) =>
  emails.length === 0 ||
  users.some((user) => emails.includes(user.Email) && user.PartnerId === partner.PartnerId)

const matchesSelectedPartnerIds = (partner: ICockpitPartnerData, selectedPartnerIds: string[]) =>
  selectedPartnerIds.length === 0 || selectedPartnerIds.includes(partner.PartnerId)

const matchesGlobalPartners = (partner: ICockpitPartnerData, globalPartners: string[]) => {
  if (globalPartners.length === 0) {
    return true
  }

  return globalPartners.some((globalPartner) =>
    globalPartner === NOT_PART_OF_GLOBAL_PARTNER
      ? !partner.GlobalPartnerId
      : partner.GlobalPartnerId === globalPartner
  )
}

const matchesMdfStatus = (partner: ICockpitPartnerData, mdfStatus: TMdfStatus) => {
  if (mdfStatus === 'All') {
    return true
  }

  const isMdf = partner.IsMDF === 1

  return mdfStatus === 'MDF' ? isMdf : !isMdf
}

const normalizeValue = (value: string) => value.trim().toLowerCase()

/** Per partner, whether any completeness item of the selected offering (of any offering in
 *  Overall and Consolidated) has `flag` set. A partner none of whose items states it is absent. */
const getCompletenessFlagByPartnerId = (
  completenessData: ICockpitCompletenessData[],
  selectedOffering: ProgramTypes,
  flag: 'IsPlanned' | 'IsRequired'
): Map<string, boolean> => {
  const isAggregateOffering = [ProgramTypes.Overall, ProgramTypes.Consolidated].includes(
    selectedOffering
  )
  const normalizedOffering = normalizeValue(selectedOffering)

  return completenessData.reduce((statusByPartnerId, entry) => {
    if (!isAggregateOffering && normalizeValue(entry.Offering) !== normalizedOffering) {
      return statusByPartnerId
    }

    const statuses = entry.CompletenessData.map((item) => item[flag]).filter(
      (status): status is 0 | 1 => status === 0 || status === 1
    )

    if (statuses.length === 0) {
      return statusByPartnerId
    }

    const partnerId = normalizeValue(entry.PartnerId)
    const isSet = statuses.some((status) => status === 1)
    statusByPartnerId.set(partnerId, Boolean(statusByPartnerId.get(partnerId)) || isSet)

    return statusByPartnerId
  }, new Map<string, boolean>())
}

const matchesPlannedStatus = (
  partner: ICockpitPartnerData,
  plannedStatus: TCockpitPlannedStatus,
  plannedStatusByPartnerId: Map<string, boolean> | null
) => {
  if (plannedStatus === 'All') {
    return true
  }

  const isPlanned = plannedStatusByPartnerId?.get(normalizeValue(partner.PartnerId))
  return isPlanned !== undefined && (plannedStatus === 'Planned' ? isPlanned : !isPlanned)
}

/** Only `IsRequired: 1` makes a partner mandatory. Unlike the planned status, a partner without
 *  the flag (no completeness entry for the offering included) is non-mandatory, so the two
 *  options together always give every partner. */
const matchesMandatoryStatus = (
  partner: ICockpitPartnerData,
  mandatoryStatus: TCockpitMandatoryStatus,
  requiredStatusByPartnerId: Map<string, boolean> | null
) => {
  if (mandatoryStatus === 'All') {
    return true
  }

  const isRequired = requiredStatusByPartnerId?.get(normalizeValue(partner.PartnerId)) === true
  return mandatoryStatus === 'Mandatory' ? isRequired : !isRequired
}

/** Whether any partner is mandatory for some offering, counted as the mandatory filter counts it
 *  in Overall. */
export const hasCockpitMandatoryPartners = (
  partnerData: ICockpitPartnerData[],
  completenessData: ICockpitCompletenessData[]
): boolean => {
  const requiredStatusByPartnerId = getCompletenessFlagByPartnerId(
    completenessData,
    ProgramTypes.Overall,
    'IsRequired'
  )

  return partnerData.some((partner) =>
    matchesMandatoryStatus(partner, 'Mandatory', requiredStatusByPartnerId)
  )
}

/** `partnerData` narrowed by RTM plus every active cockpit filter criterion. */
export const getFilteredCockpitPartnerData = ({
  partnerData,
  selectedRtm,
  selectedOffering,
  completenessData,
  filter,
  pbmUsers,
  managerUsers,
  geoMappingItems,
  partnerClassFlags,
}: IParams): ICockpitPartnerData[] => {
  const plannedStatusByPartnerId =
    filter.plannedStatus === 'All'
      ? null
      : getCompletenessFlagByPartnerId(completenessData, selectedOffering, 'IsPlanned')
  const requiredStatusByPartnerId =
    filter.mandatoryStatus === 'All'
      ? null
      : getCompletenessFlagByPartnerId(completenessData, selectedOffering, 'IsRequired')

  return partnerData
    .filter((partner) => matchesRtm(partner, selectedRtm))
    .filter((partner) => matchesGeoMappings(partner, filter.geoMappings, geoMappingItems))
    .filter((partner) => matchesAmplifyMemberships(partner, filter.amplifyMemberships))
    .filter((partner) => matchesPartnerClasses(partner, filter.partnerClasses, partnerClassFlags))
    .filter((partner) => matchesUserEmail(partner, filter.pbm, pbmUsers))
    .filter((partner) => matchesUserEmail(partner, filter.manager, managerUsers))
    .filter((partner) => matchesGlobalPartners(partner, filter.globalPartners))
    .filter((partner) => matchesSelectedPartnerIds(partner, filter.selectedPartnerIds))
    .filter((partner) => matchesMdfStatus(partner, filter.mdfStatus))
    .filter((partner) =>
      matchesPlannedStatus(partner, filter.plannedStatus, plannedStatusByPartnerId)
    )
    .filter((partner) =>
      matchesMandatoryStatus(partner, filter.mandatoryStatus, requiredStatusByPartnerId)
    )
}
